import { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react';
import { ToastContext, type ToastEntry, type ToastOptions, type ToastVariant } from './toast-context';

const VARIANT_STYLES: Record<ToastVariant, { container: string; icon: React.ReactNode }> = {
  success: {
    container:
      'bg-success-50 dark:bg-success-900/30 border-success-200 dark:border-success-800 text-success-900 dark:text-success-100',
    icon: <CheckCircle2 className="w-5 h-5 text-success-600 dark:text-success-400 flex-shrink-0" />,
  },
  error: {
    container:
      'bg-error-50 dark:bg-error-900/30 border-error-200 dark:border-error-800 text-error-900 dark:text-error-100',
    icon: <AlertCircle className="w-5 h-5 text-error-600 dark:text-error-400 flex-shrink-0" />,
  },
  info: {
    container:
      'bg-primary-50 dark:bg-primary-900/30 border-primary-200 dark:border-primary-800 text-primary-900 dark:text-primary-100',
    icon: <Info className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0" />,
  },
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const idRef = useRef(0);
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const showToast = useCallback(
    ({ title, description, variant = 'info', durationMs = 6000 }: ToastOptions) => {
      const id = ++idRef.current;
      setToasts(prev => [...prev, { id, title, description, variant, durationMs }]);
      if (durationMs > 0) {
        const timer = setTimeout(() => dismiss(id), durationMs);
        timersRef.current.set(id, timer);
      }
    },
    [dismiss],
  );

  // Clean up any pending timers on unmount so we don't try to set state on a
  // torn-down provider during HMR.
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach(clearTimeout);
      timers.clear();
    };
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div
        className="fixed top-4 right-4 z-[100] flex flex-col gap-2 max-w-sm w-[calc(100%-2rem)] sm:w-96 pointer-events-none"
        data-testid="toast-region"
      >
        {toasts.map(toast => {
          const style = VARIANT_STYLES[toast.variant];
          return (
            <div
              key={toast.id}
              role="status"
              aria-live="polite"
              data-testid={`toast-${toast.variant}`}
              className={`pointer-events-auto rounded-lg border shadow-lg px-4 py-3 flex items-start gap-3 ${style.container}`}
            >
              {style.icon}
              <div className="flex-1 min-w-0">
                {toast.title && <p className="text-sm font-medium">{toast.title}</p>}
                {toast.description && (
                  <p className={`text-sm ${toast.title ? 'mt-0.5 opacity-90' : ''}`}>{toast.description}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => dismiss(toast.id)}
                aria-label="Dismiss notification"
                data-testid="toast-dismiss-button"
                className="flex-shrink-0 -mr-1 p-1 rounded hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}
